import React, { useState, useEffect, useRef } from 'react';

const VoiceDictationButton = ({ onTranscript, currentValue = '', className = '', label = 'Dettatura vocale' }) => {
    const [isListening, setIsListening] = useState(false);
    const [isSupported, setIsSupported] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const recognitionRef = useRef(null);

    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            setIsSupported(false);
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'it-IT';

        let accumulatedTranscript = '';

        recognition.onstart = () => {
            setIsListening(true);
            setErrorMessage('');
            accumulatedTranscript = '';
        };

        recognition.onresult = (event) => {
            let newFinalChunk = '';

            for (let i = event.resultIndex; i < event.results.length; ++i) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    newFinalChunk += transcript + ' ';
                }
            }

            if (newFinalChunk) {
                onTranscript((prevValue) => {
                    const current = prevValue !== undefined ? prevValue : currentValue;
                    const needsSpace = current.length > 0 && !current.endsWith(' ') && !current.endsWith('\n');
                    return (current + (needsSpace ? ' ' : '') + newFinalChunk.trimStart());
                });
            }
        };

        recognition.onerror = (event) => {
            console.warn('Speech recognition error:', event.error);
            if (event.error === 'not-allowed') {
                setErrorMessage('Permesso negato. Clicca sull\'icona del lucchetto/impostazioni accanto all\'URL per abilitare il microfono.');
            } else if (event.error === 'no-speech' || event.error === 'aborted') {
                // Eventi normali di interruzione manuale o silenzio, non mostrare errore
                setErrorMessage('');
            } else {
                setErrorMessage('Errore microfono: ' + event.error);
            }
            setIsListening(false);
        };

        recognition.onend = () => {
            setIsListening(false);
        };

        recognitionRef.current = recognition;

        return () => {
            if (recognitionRef.current) {
                try {
                    recognitionRef.current.abort();
                } catch (e) {
                    // ignore
                }
            }
        };
    }, [onTranscript, currentValue]);

    const toggleListening = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!isSupported) {
            alert('Il riconoscimento vocale non è supportato da questo browser. Ti consigliamo di usare Google Chrome, Edge o Safari.');
            return;
        }

        if (isListening) {
            try {
                recognitionRef.current?.stop();
            } catch (err) {
                console.error("Error stopping recognition:", err);
            }
            setIsListening(false);
        } else {
            try {
                setErrorMessage('');
                // Request mic permission explicitly if mediaDevices is supported
                if (navigator.mediaDevices?.getUserMedia) {
                    try {
                        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                        // Release media stream tracks immediately so speech recognition can bind to it
                        stream.getTracks().forEach(track => track.stop());
                    } catch (permErr) {
                        console.warn("User media permission error:", permErr);
                        if (permErr.name === 'NotAllowedError' || permErr.name === 'PermissionDeniedError') {
                            setErrorMessage('Permesso negato. Clicca sull\'icona del lucchetto/impostazioni accanto all\'URL per consentire il microfono.');
                            return;
                        }
                    }
                }
                recognitionRef.current?.start();
            } catch (err) {
                console.error("Error starting recognition:", err);
                if (err.name !== 'InvalidStateError') {
                    setErrorMessage('Impossibile avviare il microfono: ' + (err.message || err.name));
                }
            }
        }
    };

    if (!isSupported) {
        return (
            <button
                type="button"
                disabled
                title="Riconoscimento vocale non supportato da questo browser"
                className={`text-xs px-2 py-1 rounded-lg bg-gray-100 text-gray-400 cursor-not-allowed inline-flex items-center gap-1 ${className}`}
            >
                <span>🎙️</span>
                <span className="hidden sm:inline">Microfono non supportato</span>
            </button>
        );
    }

    return (
        <div className="inline-flex items-center gap-1.5">
            <button
                type="button"
                onClick={toggleListening}
                title={isListening ? "Clicca per terminare la dettatura" : "Clicca per avviare la dettatura vocale in italiano"}
                className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-all duration-200 inline-flex items-center gap-1.5 shadow-sm ${
                    isListening
                        ? 'bg-rose-500 hover:bg-rose-600 text-white animate-pulse ring-2 ring-rose-300'
                        : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200'
                } ${className}`}
            >
                {isListening ? (
                    <>
                        <span className="w-2 h-2 rounded-full bg-white animate-ping"></span>
                        <span>🔴 In ascolto... (Stop)</span>
                    </>
                ) : (
                    <>
                        <span>🎙️</span>
                        <span>{label}</span>
                    </>
                )}
            </button>
            {errorMessage && (
                <span className="text-[11px] text-rose-500 font-medium">{errorMessage}</span>
            )}
        </div>
    );
};

export default VoiceDictationButton;
